/** Scope-Filter-Berechnung fuer Chat-Granularitaet (DD8). */
import type { ChatFacets, ScopeFilter } from '@/types/teacher-chat'

/** Aktive Facetten-Auswahl pro Dimension (Mengen von Werten/IDs). */
export interface ActiveScopeFilter {
  subjects: Set<string>
  classes: Set<string>
  days: Set<string>
  teachers: Set<string>
}

export function emptyActiveFilter(): ActiveScopeFilter {
  return { subjects: new Set(), classes: new Set(), days: new Set(), teachers: new Set() }
}

export function isFilterEmpty(f: ActiveScopeFilter): boolean {
  return (
    f.subjects.size === 0 &&
    f.classes.size === 0 &&
    f.days.size === 0 &&
    f.teachers.size === 0
  )
}

/**
 * Berechnet ScopeFilter (Backend-Vertrag DD8) aus aktiven Facetten.
 * - Fach/Klasse/Tag/Lehrer-Dimensionen werden als UND-Schnittmenge ihrer lesson_ids aufgeloest.
 * - Bei genau 1 aktivem Lehrer wird teacher_filter_id gesetzt (fuer sender-basierte Sichtbarkeit);
 *   bei mehreren Lehrern nur deren vereinte lesson_ids.
 */
export function computeScopeFilter(
  facets: ChatFacets | null | undefined,
  active: ActiveScopeFilter
): ScopeFilter {
  if (!facets || isFilterEmpty(active)) return {}

  // B1-Fix: Filter ist aktiv sobald IRGENDEINE Facette aktiv ist, unabhaengig
  // davon, ob die Intersektion Lektionen enthaelt (leere Menge = kein Treffer).
  const scope_filter_active = true

  // Pro aktive Dimension: vereinigte lesson_ids (null = Dimension inaktiv).
  const subjectIds = active.subjects.size
    ? unionLessonIds(facets.subjects.filter(e => active.subjects.has(e.name)))
    : null
  const classIds = active.classes.size
    ? unionLessonIds(facets.classes.filter(e => active.classes.has(e.name)))
    : null
  const dayIds = active.days.size
    ? unionLessonIds(facets.days.filter(e => active.days.has(e.name)))
    : null
  const teacherIds = active.teachers.size
    ? unionLessonIds(facets.teachers.filter(e => active.teachers.has(e.teacher_id)))
    : null

  // UND-Schnittmenge aller aktiven Dimensionen (kann leer sein = B1).
  const dimSets = [subjectIds, classIds, dayIds, teacherIds].filter(
    (s): s is Set<string> => s !== null
  )
  const intersection = intersectAll(dimSets)

  const scope_filter_lesson_ids = intersection ? [...intersection] : []
  // Genau 1 Lehrer aktiv -> teacher_filter_id (sender-basierte Sichtbarkeit DD5).
  const teacher_filter_id =
    active.teachers.size === 1 ? [...active.teachers][0] : undefined
  return { scope_filter_active, scope_filter_lesson_ids, teacher_filter_id }
}

function unionLessonIds(entries: { lesson_ids: string[] }[]): Set<string> {
  const s = new Set<string>()
  for (const e of entries) for (const id of e.lesson_ids) s.add(id)
  return s
}

function intersectAll(sets: Set<string>[]): Set<string> | null {
  if (sets.length === 0) return null
  let result = new Set(sets[0])
  for (let i = 1; i < sets.length; i++) {
    const next = sets[i]!
    result = new Set([...result].filter(x => next.has(x)))
  }
  return result
}

/** Stabiler String-Key fuer React-Query (verhindert Cache-Kollisionen gefiltert/ungefiltert). */
export function scopeFilterKey(filter: ScopeFilter): string {
  const active = filter.scope_filter_active ? '1' : '0'
  const ids = (filter.scope_filter_lesson_ids ?? []).slice().sort().join(',')
  const tid = filter.teacher_filter_id ?? ''
  return `${active}|${ids}|${tid}`
}

/** Kurzlabel fuer eine Lektion (Badge-Anzeige) + Tag-Facet-Label (Dup#1 Single Source). */
export function lessonShortLabel(l: {
  date?: string | null
  time_slot_name?: string | null
  subject_name?: string | null
  klass_name?: string | null
}): string {
  const parts: string[] = []
  if (l.date) parts.push(formatDayShort(l.date))
  if (l.time_slot_name) parts.push(l.time_slot_name)
  if (l.subject_name) parts.push(l.subject_name)
  if (l.klass_name) parts.push(l.klass_name)
  return parts.join(' · ')
}

export function formatDayShort(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })
  } catch {
    return iso
  }
}
